import * as d3 from 'd3';

import { Circle, G, Rect, Svg, Text as SvgText } from 'react-native-svg';
import { ColorHelper, UIHelper as uh } from '../../../core';
import { LayoutChangeEvent, Platform, View, ViewProps } from 'react-native';

import { NewHealthMarker } from 'src/core/types/HealthMarkerReport';
import React from 'react';
import { useTheme } from '@ui-kitten/components';
import { toPreciseNumber } from 'src/core/helpers/NumberHelper';

const GRAPH_BAR_HEIGHT = 8;

//props
interface IBarChartProps extends ViewProps {
  currentValue: number;
  healthMarker: NewHealthMarker;
  factor: number;
}

const BarChart = (props: IBarChartProps) => {
  const th = useTheme();
  const [width, setWidth] = React.useState(uh.currentViewPort());
  const graphHeight = 48;
  const graphWidth = width;
  const graphData = [];
  const textRanges = ['Low', 'Optimal', 'High', 'Average'];
  for (const category of props.healthMarker.categories) {
    let label = category.rangeLabel != '' ? category.rangeLabel : category.rangeLabelAlt;
    const data = {
      label:
        textRanges.includes(label) == true
          ? label
          : `${toPreciseNumber(Number(category.minimum) * props.factor)}-${toPreciseNumber(Number(category.maximum) * props.factor)}`,
      value: Number(category.maximum) * props.factor - Number(category.minimum) * props.factor,
      description: '',
      color:
        props.healthMarker.pillar == undefined
          ? category.color
          : th[ColorHelper.getBarColor(category.color, props.healthMarker.pillar.toLocaleLowerCase())]
    };
    graphData.push(data);
  }
  const spacingInner = 2.0;
  const onLayout = (event: LayoutChangeEvent) => {
    setWidth(event.nativeEvent.layout.width);
  };

  // X scale linear
  const xDomain = [0, graphData.reduce((total: number, thing: any) => total + thing.value, 0)];
  const xRange = [0, graphWidth - spacingInner * (graphData.length - 1)];
  const x = d3.scaleLinear().domain(xDomain).range(xRange);
  let value = 0;
  const minValue = Number(props.healthMarker?.categories[0]?.minimum) * props.factor;
  const maxValue = Number(props.healthMarker?.categories[graphData?.length - 1]?.maximum) * props.factor;
  let currentValue =
    props.currentValue * props.factor - minValue < 0 ? 0 : props.currentValue * props.factor - minValue;
  currentValue = props.currentValue * props.factor > maxValue ? maxValue - minValue : currentValue;
  let xValue = x(currentValue);
  const xMaxValue = x(maxValue - minValue);
  if (xValue > xMaxValue - 4) {
    xValue = xMaxValue - (Platform.OS === 'android' ? 6 : 4);
  }
  if (xValue < x(0) + 7) {
    xValue = x(0) + (Platform.OS === 'android' ? 9 : 7);
  }

  return (
    <View style={[props.style]} onLayout={(event) => onLayout(event)}>
      <Svg width={graphWidth} height={graphHeight} fill="#c4c4c4">
        <G y={20}>
          {/* bars */}
          {graphData.map((item: any, index: number) => {
            const oldValue = value;
            value += item.value;
            const barWidth = x(item.value);
            const barX = x(oldValue) + index * spacingInner * 1.0;
            const textX = barX + barWidth / 2;
            return (
              <G key={`barchart-${index}`}>
                {/* description */}
                <SvgText
                  x={textX}
                  y={-15}
                  scale={1}
                  fill={th['text-hint-color']}
                  fontSize={10}
                  fontFamily={'Poppins-Regular'}
                  textAnchor={'middle'}
                  alignmentBaseline={'central'}>
                  {item.description}
                </SvgText>

                <Rect
                  key={item.label}
                  y={0}
                  x={barX}
                  rx={4}
                  ry={4}
                  width={barWidth}
                  height={GRAPH_BAR_HEIGHT}
                  fill={item.color}
                />
                {/* label */}
                <SvgText
                  x={textX}
                  y={21}
                  scale={1}
                  fill={th['text-hint-color']}
                  fontSize={10}
                  textAnchor={'middle'}
                  fontFamily={'Poppins-Regular'}
                  alignmentBaseline={'central'}>
                  {item.label}
                </SvgText>
              </G>
            );
          })}
          {/* circle */}
          <Circle cy={4} cx={xValue} r={8} fill={th['color-primary-500']} stroke={'white'} strokeWidth={2} />
        </G>
      </Svg>
    </View>
  );
};

export default BarChart;
